import React from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useLiveEntityRelationships } from '@/hooks/useLiveEntityRelationships';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { objectsStore } from "@/lib/stores/objectsStore";
import { documentStore } from '@/lib/stores/documentStore';
import { templatesStore } from "@/lib/stores/templatesStore";
import { ChevronRight } from 'lucide-react';
import IconProvider from '@/components/ui/IconProvider';
import { getEntityIcon } from '@/lib/config/pageItemConfig';

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
export function ObjectLinkBlock() {
  const { t } = usePlatformTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const { selectedGame } = useSimplifiedUserData();

    // Determine current entity from route
    const getCurrentEntity = () => {
        const path = location.pathname;
        
        if (path.startsWith('/documents/') && params.documentId) {
            return {
                entityId: params.documentId,
                entityType: 'document' as const,
                detectLiveReferences: true
            };
        }
        
        if (path.startsWith('/library/') && params.itemId) {
            return {
                entityId: params.itemId,
                entityType: 'glossary' as const,
                detectLiveReferences: false
            };
        }
        
        if (path.startsWith('/blueprints/') && params.blueprintId) {
            return {
                entityId: params.blueprintId,
                entityType: 'blueprint' as const,
                detectLiveReferences: false
            };
        }
        
        return null;
    };

    const currentEntity = getCurrentEntity();

    const { relationships, loading } = useLiveEntityRelationships(
        currentEntity?.entityId,
        currentEntity?.entityType,
        selectedGame?.id,
        currentEntity?.detectLiveReferences || false
    );

    // Get entity name based on type and id
    const getEntityName = (id: string, type: string): string => {
        switch (type) {
            case 'glossary': {
                const item = objectsStore.glossaryItems?.find(g => g.id === id);
                return item?.name || 'Unknown Item';
            }
            case 'document': {
                const doc = documentStore.documents?.find(d => d.id === id);
                return doc?.name || 'Unknown Document';
            }
            case 'blueprint': {
                const bp = templatesStore.blueprints?.find(b => b.id === id);
                return bp?.name || 'Unknown Blueprint';
            }
            default:
                return `Unknown ${type}`;
        }
    };

    // Get icon based on entity type (using centralized config)
    const getEntityIconComponent = (type: string) => {
        const iconString = getEntityIcon(type);
        // Color mapping for entity types
        const colorClasses: Record<string, string> = {
            'glossary': 'text-purple-600',
            'document': 'text-blue-600',
            'blueprint': 'text-green-600',
            'type': 'text-orange-600',
        };
        const colorClass = colorClasses[type] || 'text-gray-600';
        
        return <IconProvider iconstring={iconString} size={16} className={colorClass} />;
    };

    // Navigate to entity detail page
    const navigateToEntity = (id: string, type: string) => {
        switch (type) {
            case 'glossary':
                navigate(`/library/${id}`);
                break;
            case 'document':
                navigate(`/documents/${id}`);
                break;
            case 'blueprint':
                navigate(`/blueprints/${id}`);
                break;
            default:
                console.log(`Navigation not implemented for type: ${type}`);
        }
    };

    if (!currentEntity || !relationships) {
        return null;
    }

    const hasReferencedBy = relationships.referenced_by && relationships.referenced_by.length > 0;
    const hasReferences = relationships.references && relationships.references.length > 0;
    const hasLiveReferences = (relationships as any).live_references_count > 0;

    if (loading) {
        return (
            <div className="space-y-2 mt-2">
                <div className="h-10 bg-gray-100 rounded animate-pulse"></div>
                <div className="h-10 bg-gray-100 rounded animate-pulse"></div>
            </div>
        );
    }

    return (
        <div className="mt-4 space-y-4">
            {/* Referenced By / Used In Section */}
            {hasReferencedBy && (
                <div>
                    <h5 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                        Used In ({relationships.referenced_by_count})
                    </h5>
                    <div className="space-y-1">
                        {relationships.referenced_by.map((ref, index) => (
                            <button
                                key={`${ref.source_id}-${index}`}
                                onClick={() => navigateToEntity(ref.source_id, ref.source_type)}
                                className="w-full flex items-center justify-between p-2 rounded-md bg-white hover:bg-gray-50 border border-gray-200 transition-colors group"
                            >
                                <div className="flex items-center gap-2 min-w-0">
                                    {getEntityIconComponent(ref.source_type)}
                                    <span className="text-sm text-gray-900 truncate">
                                        {getEntityName(ref.source_id, ref.source_type)}
                                    </span>
                                </div>
                                <ChevronRight 
                                    size={14} 
                                    className="text-gray-400 group-hover:text-gray-600 transition-colors flex-shrink-0"
                                />
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* References Section */}
            {hasReferences && (
                <div>
                    <div className="flex items-center gap-2 mb-2">
                        <h5 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                            References ({relationships.references_count})
                        </h5>
                        {hasLiveReferences && (
                            <div className="flex items-center gap-1 px-1.5 py-0.5 bg-green-100 rounded text-xs text-green-700">
                                <Zap size={10} />
                                <span>{t('platform.layout.structure-menu.live')}</span>
                            </div>
                        )}
                    </div>
                    <div className="space-y-1">
                        {relationships.references.map((ref, index) => (
                            <button
                                key={`${ref.target_id}-${index}`}
                                onClick={() => navigateToEntity(ref.target_id, ref.target_type)}
                                className={`w-full flex items-center justify-between p-2 rounded-md transition-colors group ${
                                    ref.isLive 
                                        ? 'bg-green-50 hover:bg-green-100 border border-green-200' 
                                        : 'bg-white hover:bg-gray-50 border border-gray-200'
                                }`}
                            >
                                <div className="flex items-center gap-2 min-w-0">
                                    {getEntityIconComponent(ref.target_type)}
                                    <span className={`text-sm truncate ${ref.isLive ? 'text-green-900' : 'text-gray-900'}`}>
                                        {getEntityName(ref.target_id, ref.target_type)}
                                    </span>
                                    {ref.isLive && (
                                        <span className="text-xs text-green-600 italic flex-shrink-0">
                                            (unsaved)
                                        </span>
                                    )}
                                </div>
                                <ChevronRight 
                                    size={14} 
                                    className={`transition-colors flex-shrink-0 ${
                                        ref.isLive 
                                            ? 'text-green-400 group-hover:text-green-600' 
                                            : 'text-gray-400 group-hover:text-gray-600'
                                    }`}
                                />
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}